import { useMemo, useState, useEffect } from 'react';
import useWorkerSubstancesForm from './useWorkerSubstancesForm';
import useAlerts from './useAlerts';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../../hooks/useTranslate';
import {
    useEditCarcinogenicReportWorkerDetailMutation,
    useGetCarcinogenicReportWorkerDetailQuery
} from '../../../../../services/carcinogenicApi';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import { EXPOSITION_OPTIONS } from '../consts';
import { esIngresoEnAnioEnCurso } from '../helpers';

const getInitialDate = (date: string): string => {
    if (!date) return '';

    const utcDate = new Date(date);
    return utcDate.toISOString().split('T')[0];
};

const findItem = (array: any[], value: any) => {
    return array.find((item) => item.value === value);
};

const useMultipleWorkerSubstancesForms = (selectedWorker: any, handleCloseDrawer: any) => {
    const dispatch = useAppDispatch();
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const { data: workerDetails, isFetching: isFetchingWorkerDetail } =
        useGetCarcinogenicReportWorkerDetailQuery(
            {
                wokerId: selectedWorker?.id,
                reportId: selectedCarcinogenicReportId
            },
            { skip: !selectedWorker?.id }
        );

    // const { workerDetails, isFetchingWorkerDetail } = useWorkerDetail(selectedWorker?.id);
    const expositions = workerDetails?.data?.exposiciones || [];
    const isExposed = selectedWorker?.exposiciones?.length > 0;
    const ingressDate = selectedWorker?.fechaIngreso
        ? getInitialDate(selectedWorker.fechaIngreso)
        : '';

    const [expositionType, setExpositionType] = useState(
        findItem(EXPOSITION_OPTIONS, workerDetails?.data?.permanente) || ''
    );

    // Update expositionType when workerDetails changes
    useEffect(() => {
        if (workerDetails?.data) {
            setExpositionType(findItem(EXPOSITION_OPTIONS, workerDetails.data.permanente) || '');
        }
    }, [workerDetails?.data]);

    const handleExpositionTypeChange = (newValue: any) => {
        setExpositionType(newValue);
    };

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.workersAndMedicalExams'
    });
    const { translate } = useTranslate(t);
    const [editWorkerDetail, { isLoading: isEditingWorkerDetail }] =
        useEditCarcinogenicReportWorkerDetailMutation();

    // Process the substances data
    const substancesData = useMemo(() => {
        return expositions.map((exposition: any) => ({
            expositionDateStart: getInitialDate(exposition?.fechaInicio) || '',
            expositionDateEnd: (exposition?.fechaFin && getInitialDate(exposition?.fechaFin)) || '',
            medicalExams: exposition?.examenesMedico || '',
            substanceId: exposition?.id,
            // Fix these lines to properly access nested properties
            substanceCode: exposition?.sustancia?._riesgo?.codigo || '',
            substanceDescription: exposition?.sustancia?._riesgo?.descripcion || ''
        }));
    }, [workerDetails]);

    const { showConfirmEditWorkerAlert } = useAlerts({ translate });

    // Create a single form instance that handles all substances
    // Only use the form if the worker is exposed
    const {
        formsData,
        errors,
        hasValidationErrors,
        handleChange,
        handleSubmitSubstancesAndMedicalExaminations,
        formConfigs,
        resetValidation
    } = useWorkerSubstancesForm(isExposed ? substancesData : [], isExposed ? ingressDate : '');

    const handleEditWorkerSubstancesAndMedicalExams = async () => {
        try {

            const adaptedFormsData = formsData?.map((formData: any) => {
                return {
                    id: formData.substanceId,
                    inicio: formData.expositionDateStart,
                    fin: formData.expositionDateEnd || null,
                    idsExamenMedico: formData.medicalExams.map((exam: any) => exam.id)
                };
            });

            const response: any = await editWorkerDetail({
                reportId: selectedCarcinogenicReportId,
                workerId: workerDetails?.data?.id,
                body: {
                    permanente: expositionType?.value,
                    exposiciones: adaptedFormsData
                }
            });

            if (response?.error) {
                enqueueSnackbar(translate('enqueues.edit.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.edit.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudieron actualizar las sustancias y exámenes del trabajador',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
        handleCloseDrawer();
    };

    const handleSubmit = async () => {
        if(!isExposed) {
            handleCloseDrawer();
            return;
        }

        if (esIngresoEnAnioEnCurso(ingressDate)) {
            enqueueSnackbar(translate('enqueues.invalidIngressYear.error'), {
                variant: 'error'
            });
            return;
        }

        const isValid = handleSubmitSubstancesAndMedicalExaminations();
        if (!isValid) {
            return;
        }

        showConfirmEditWorkerAlert({
            confirmAction: handleEditWorkerSubstancesAndMedicalExams
        });

    };

    return {
        formsData,
        errors,
        hasValidationErrors,
        handleChange,
        isExposed,
        handleSubmit,
        expositionType,
        isEditingWorkerDetail,
        workerDetails,
        handleExpositionTypeChange,
        isFetchingWorkerDetail,
        formConfigs,
        resetValidation
    };
};

export default useMultipleWorkerSubstancesForms;


